// TODO: Create a common Table component to cater to such requirements as part of the design system
/**
 *  Component: TagsList
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { Table, TableBody, TableRow, TablePagination } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import Skeleton from '@material-ui/lab/Skeleton';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import { closeRealignModalAction } from 'components/RealignModal/redux/actions';
import TimeBoundSelect from 'components/TimeBoundSelect/TimeBoundSelect';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import OrgObjective from 'models/orgObjective';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { Button } from 'styles/admin';
import {
  Divider1,
  FlexContainer,
  SpaceBetweenContainer,
  StyledBox,
  StyledTypography,
  VerticalFlexContainer,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import LostPerson from 'assets/images/illustrations/lostPerson.png';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getColorFromKey, getTimePeriodIdString } from 'constants/helper';
import { CrossIcon, PlusIcon2 } from 'constants/icons';
import { sendClickedOnTimePeriodEvent, sendUpdatedObjectiveEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import {
  getTimeBoundTimePeriods,
  getTimeBoundObjectives,
  importTimePeriodObjectives,
  saveObjective,
  updateObjective,
} from 'services/admin';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { ButtonOutlinedHoverFilled } from 'uiToolkit/Button';
import SearchTextField from 'uiToolkit/SearchTextField';
import { Typography10, Typography4, Typography6, Typography9 } from 'uiToolkit/Typography';

import NewEditableTagRow, { paddingFactor } from './NewEditableTagRow';
import { SkeletonContainer, StyledTableHead, HeaderTableCell, StyledTableRow, StyledTableCell } from './styles';

const filterTimePeriod = (timePeriodData, selectedTimeBoundPeriod) => {
  const isSameAsSelected = (timeBound) =>
    getTimePeriodIdString(timeBound) === getTimePeriodIdString(selectedTimeBoundPeriod);

  return {
    previous: isSameAsSelected(timePeriodData.previous) ? null : timePeriodData.previous,
    current: isSameAsSelected(timePeriodData.current) ? null : timePeriodData.current,
    next: isSameAsSelected(timePeriodData.next) ? null : timePeriodData.next,
    other: timePeriodData.other.filter((timePeriod) => !isSameAsSelected(timePeriod)),
  };
};

const HeaderComponent = ({ modalView, isModal }) => (
  <StyledTableHead>
    <TableRow>
      <HeaderTableCell $isSticky={isModal} paddingtop="0.75rem" paddingBottom="0.75rem" paddingLeft="24px" width="40%">
        <Typography10 customColor={theme.palette.text.dark1}>Objective Name</Typography10>
      </HeaderTableCell>
      {!modalView && (
        <HeaderTableCell paddingtop="0.75rem" paddingBottom="0.75rem" paddingLeft="26px" width="25%">
          <Typography10 customColor={theme.palette.text.dark1}>Objective Level</Typography10>
        </HeaderTableCell>
      )}
      <HeaderTableCell $isSticky={isModal} paddingtop="0.75rem" paddingBottom="0.75rem" paddingLeft="28px" width="25%">
        <Typography10 customColor={theme.palette.text.dark1}>Description</Typography10>
      </HeaderTableCell>
      <HeaderTableCell $isSticky={isModal} paddingtop="0.75rem" paddingBottom="0.75rem" width="12%" />
    </TableRow>
  </StyledTableHead>
);

const StyledBottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const StyledAddIcon = styled(AddRoundedIcon)`
  border-radius: 50%;
  fill: ${(props) => props.theme.palette.primary.main};
  border: ${(props) => `1px solid ${props.theme.palette.primary.main}`};
  padding: 2px;
`;

const StyledImage = styled.img`
  width: 20%;
  position: relative;
`;

const StyledImage2 = styled.img`
  width: 35%;
  position: relative;
`;

const TableHeaderWrapper = styled(SpaceBetweenContainer)`
  padding: 16px 24px;
  height: 73px;
  border-bottom: 1px solid ${(props) => props.theme.palette.borders.lighterGrey};
  ${(props) =>
    props.$stickyHeader &&
    css`
      position: sticky;
      top: 0;
      z-index: 10;
      background: ${theme.palette.background.paper};
    `}
`;

export const TimePeriodDropDown = ({
  timePeriodData = {},
  selectedTimeBoundPeriod,
  setSelectedTimeBoundPeriod,
  width = '160px',
  disabled = false,
  isUnderlineStyle = false,
  isStyle2 = false,
}) => {
  const { previous, current, next, other = [] } = timePeriodData;
  const previousCurrentNext = [previous, current, next].filter((item) => Boolean(item));
  const allTimePeriods = [previous, current, next, ...other].filter((item) => Boolean(item));

  const menuItems = previousCurrentNext.map((timeBound) => {
    const { timelineTag, startTS, endTS } = timeBound;
    return {
      value: getTimePeriodIdString({ timelineTag, startTS, endTS }),
      label: timelineTag,
      tag: timeBound === current ? 'Current' : '',
    };
  });

  const otherMenuItems = other.map(({ startTS, endTS, timelineTag }) => ({
    value: getTimePeriodIdString({ timelineTag, startTS, endTS }),
    label: timelineTag,
  }));
  const currentSelectedValue = getTimePeriodIdString(selectedTimeBoundPeriod);

  const handlePeriodChange = (selectedTimePeriodIdString) => {
    const selectedTimePeriodObject = allTimePeriods.find(
      ({ startTS, endTS, timelineTag }) =>
        selectedTimePeriodIdString === getTimePeriodIdString({ timelineTag, startTS, endTS })
    );
    setSelectedTimeBoundPeriod(selectedTimePeriodObject);
  };

  return (
    <TimeBoundSelect
      selectedValue={currentSelectedValue}
      onChange={({ target }) => {
        handlePeriodChange(target.value);
      }}
      menuItems={menuItems}
      otherMenuItems={otherMenuItems}
      width={width}
      disabled={disabled}
      isUnderlineStyle={isUnderlineStyle}
      isStyle2={isStyle2}
    />
  );
};

const CreateNewObjectiveRow = ({ displayNewTagCreationRow }) => (
  <StyledTableRow style={{ height: '62px' }}>
    <StyledTableCell paddingtop="10px" paddingBottom="6px" paddingLeft={`${paddingFactor}px`} colSpan={4} border>
      <StyledBox
        display="inline-flex"
        alignItems="center"
        onClick={displayNewTagCreationRow}
        style={{ cursor: 'pointer' }}
      >
        <CircularIcon color="primary">
          <StyledPlusIcon />
        </CircularIcon>
        <StyledTypography variant="body2" color="primary" leftGutter={3}>
          Create New Objective
        </StyledTypography>
      </StyledBox>
    </StyledTableCell>
  </StyledTableRow>
);

const Loader = ({ singleLine = false }) => {
  if (singleLine) {
    return (
      <SkeletonContainer>
        <Skeleton width="60%" />
      </SkeletonContainer>
    );
  }
  return (
    <SkeletonContainer>
      <Skeleton width="60%" />
      <Skeleton width="40%" />
      <Skeleton width="50%" />
    </SkeletonContainer>
  );
};

const ButtonContainer2 = styled.div`
  button {
    padding: 0.3rem 0;
    width: 100%;
  }
  flex-basis: 50%;
  margin-left: 24px;
`;

const DropDownContainer = styled.div`
  flex-basis: 50%;
`;

const CircularIcon = styled(Avatar)`
  width: 24px;
  height: 24px;

  background-color: transparent;
  color: ${(props) => getColorFromKey(props.color)};

  border: ${(props) => `1px solid ${getColorFromKey(props.color)}`};
`;

const StyledPlusIcon = styled(PlusIcon2)`
  height: 10px;
  width: 10px;

  fill: ${(props) => props.theme.palette.primary.main};
`;

const getCurrentSelectedPage = (selectedObjectiveTopLevelId, allTags, rowsPerPage) => {
  const selectedObjectiveTopLevelIndex = allTags.findIndex((tag) => tag.objectiveId === selectedObjectiveTopLevelId);
  if (selectedObjectiveTopLevelIndex === -1) {
    console.error(
      `Objective id ${selectedObjectiveTopLevelId} from objective hierarchy not found in current objectives, please check hierarchy api call data`
    );
    return 0;
  }
  const pageNumber = Math.floor(selectedObjectiveTopLevelIndex / rowsPerPage);
  return pageNumber;
};

export const NoObjectivesView = ({
  timePeriodData,
  selectedTimeBoundPeriod = null,
  onImport,
  onNewClick,
  modalView = false,
  allowTimePeriodChange = true,
  enableCreation = true,
  onClose,
}) => {
  const { previous, current, next, other } = timePeriodData;
  const allTimePeriods = [previous, current, next, ...other].filter((item) => item !== null);
  const dispatch = useDispatch();

  const [isOpenCreateGoalTreeViewPanel, setIsOpenCreateGoalTreeViewPanel] = useState(false);
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);
  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [selectedSourceTimeBoundPeriod, setSelectedSourceTimeBoundPeriod] = useState(
    () =>
      allTimePeriods.filter(
        (timeBound) => getTimePeriodIdString(timeBound) !== getTimePeriodIdString(selectedTimeBoundPeriod)
      )?.[0] || null
  );
  // source time periods can be any of except the current selected

  const sourceTimePeriodData = filterTimePeriod(timePeriodData, selectedTimeBoundPeriod);

  const onImportHandler = () => {
    onImport({
      sourceTimePeriod: selectedSourceTimeBoundPeriod,
      targetTimePeriod: selectedTimeBoundPeriod,
    });
  };

  const closeRealignModal = () => {
    dispatch(closeRealignModalAction());
  };

  return (
    <FlexContainer
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        margin: !modalView ? '64px 0' : '0',
      }}
    >
      <StyledImage2 src={LostPerson} />

      <VerticalFlexContainer style={{ padding: '40px', paddingLeft: '10px' }}>
        <Typography6>No {modifyString(objectivesName, StringModifier.SENTENCE_CASE)} found</Typography6>
        {enableCreation ? (
          <Typography9 topGutter={1} bottomGutter={3} customColor={theme.palette.text.dark1}>
            No {modifyString(objectivesName, StringModifier.SENTENCE_CASE)} found for this timeline. You can import{' '}
            {modifyString(objectivesName, StringModifier.SENTENCE_CASE)} from previous timeline.
          </Typography9>
        ) : (
          <>
            <Typography9 topGutter={1} bottomGutter={3} customColor={theme.palette.text.dark1}>
              Change the timeline or ask your manager to create some.
            </Typography9>
            {/* <ButtonFilled
              variant="rowButtonFilled"
              StartIcon={Plus}
              onClick={() => {
                closeRealignModal();
                setIsOpenCreateObjectivePanel(true);
              }}
              pt="2px"
            >
              Add New
            </ButtonFilled> */}
          </>
        )}
        {enableCreation && (
          <FlexContainer style={{ height: '40px', justifyContent: 'space-evenly' }}>
            <DropDownContainer>
              <TimePeriodDropDown
                timePeriodData={sourceTimePeriodData}
                selectedTimeBoundPeriod={selectedSourceTimeBoundPeriod}
                setSelectedTimeBoundPeriod={(timePeriod) => {
                  sendClickedOnTimePeriodEvent({
                    eventProperties: {
                      source: 'Settings',
                    },
                  });
                  setSelectedSourceTimeBoundPeriod(timePeriod);
                }}
                width="200px"
                disabled={!allowTimePeriodChange}
              />
            </DropDownContainer>
            <ButtonContainer2>
              <Button blue size="large" onClick={onImportHandler}>
                Import Past Objectives
              </Button>
            </ButtonContainer2>
          </FlexContainer>
        )}
        {enableCreation && (
          <>
            <Divider1 topGutter={3} bottomGutter={3} />
            <Typography9 bottomGutter={3} customColor={theme.palette.text.dark1} align="center">
              Or you can create new objective here
            </Typography9>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ButtonOutlinedHoverFilled
                fullWidth
                onClick={onNewClick}
                color={theme.palette.primary.main}
                borderColor={theme.palette.primary.main}
                style={{ maxWidth: '300px' }}
              >
                <Typography4 customColor="inherit" lineheight="22px">
                  Create New Objective
                </Typography4>
              </ButtonOutlinedHoverFilled>
            </div>
          </>
        )}

        <ObjectiveSidePanel
          open={isOpenCreateObjectivePanel}
          onCancel={() => {
            setTimeboundDetailsOfCreatedObjective(null);

            setIsOpenCreateObjectivePanel(false);
          }}
          selectedTimeBoundPeriod={timePeriodData?.[0] ?? null}
          isAlignmentAtParentLevel
          // setIsOpenCreateGoalPanel={value => {
          //   setIsOpenCreateGoalPanel(value);
          //   setIsGoalPanelOfCreatedObjective(value);
          // }}
          setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
          source="Team Page"
        />
      </VerticalFlexContainer>
    </FlexContainer>
  );
};

const getSearchAppliedTags = (tags, searchString) =>
  !searchString
    ? tags
    : tags.filter(({ displayName }) => displayName.toLowerCase().indexOf(searchString.toLowerCase()) !== -1);

const TagsList = ({
  modalView = true,
  createNewParentTag: addNew = false,
  setCreateNewParentTag: setAddNew,
  onSelect = () => {},
  enableCreation = true,
  selectedTimeBoundPeriod = null,
  setSelectedTimeBoundPeriod,
  allowTimePeriodChange = true,
  selectedObjectiveHierarchy = [],
  selectedObjective = null,
  onNewClick = () => {},
  isModal = false,
  onClose = () => {},
}) => {
  const [timePeriodData, setTimePeriodData] = useState([]);
  const [searchString, setSearchString] = useState('');

  const [editingTags, setEditingTags] = useState([]);
  const [renamingTags, setRenamingTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [newTag, setNewTag] = useState(new OrgObjective({}));
  const rowsPerPageOptions = [5, 10, 15];
  // to keep track of auto-navigation to selected objective page, it should only happen first time
  const [isNavigatedToSelected, setIsNavigatedToSelected] = useState(false);
  const [isScrolledToSelected, setIsScrolledToSelected] = useState(false);

  const dispatch = useDispatch();

  const timePeriodsList = useMemo(() => {
    if (Array.isArray(timePeriodData)) return [];

    const { previous, current, next, other } = timePeriodData;
    const allTimePeriods = [previous, current, next, ...other].filter((item) => item !== null);
    return allTimePeriods;
  }, [timePeriodData]);

  // const searchAppliedTags = getSearchAppliedTags(allTags, searchString);

  // console.log('editingTags', editingTags);
  const setPaginatedTags = (page, orgTags, perPage) => {
    if (orgTags) {
      const start = page * perPage;
      const end = start + perPage;
      const searchAppliedTags = getSearchAppliedTags(orgTags, searchString);

      setTags(searchAppliedTags.slice(start, end));
    }
  };

  useEffect(() => {
    if (
      selectedObjective &&
      allTags.length !== 0 &&
      getTimePeriodIdString(selectedObjective.timeBoundDetails) === getTimePeriodIdString(selectedTimeBoundPeriod)
    ) {
      if (isNavigatedToSelected) {
        return;
      }
      const selectedPage = getCurrentSelectedPage(
        selectedObjectiveHierarchy?.[0] || selectedObjective.objectiveId,
        allTags,
        rowsPerPage
      );
      setPage(selectedPage);
      setPaginatedTags(selectedPage, allTags, rowsPerPage);
      setIsNavigatedToSelected(true);
    }
  }, [allTags]);

  useEffect(() => {
    setPaginatedTags(page, allTags, rowsPerPage);
  }, [searchString]);

  const getObjectiveData = ({ timeBound = selectedTimeBoundPeriod, page = 0 } = {}) => {
    setLoading(true);
    getTimeBoundObjectives({ timePeriodObj: timeBound })
      .then((objectives) => {
        setAllTags(
          objectives.map((tag) => ({
            ...tag,
            isEditing: false,
            hasChildTags: true,
          }))
        );
        setPaginatedTags(page, objectives, rowsPerPage);
      })
      .then(() => {})
      .catch((err) => dispatch(showErrorSnackbar(err.message)))
      .finally(() => setLoading(false));
  };

  const getTimePeriodsData = () => {
    getTimeBoundTimePeriods()
      .then((timePeriodData) => {
        !selectedTimeBoundPeriod && setSelectedTimeBoundPeriod(timePeriodData.current);
        setTimePeriodData(timePeriodData);
        // return selectedTimeBoundPeriod || currentTimeBound;
      })
      // .then(currentTimeBound => {
      //   getObjectiveData({ timeBound: currentTimeBound, page: 0 });
      // })
      .catch((err) => dispatch(showErrorSnackbar(err.message)));
  };

  // get tags
  useEffect(() => {
    getTimePeriodsData();
  }, []);

  useEffect(() => {
    selectedTimeBoundPeriod && getObjectiveData({ timeBound: selectedTimeBoundPeriod, page: 0 });
  }, [selectedTimeBoundPeriod]);

  // update tags
  const updateTag = (tag, updateAllInstances = false, renameObjective = false) => {
    const oldTagData = tags.filter((oldTag) => oldTag.id === tag.id);

    sendUpdatedObjectiveEvent({
      eventProperties: {
        fieldType: 'all',
        oldValue: JSON.stringify({
          description: oldTagData?.[0]?.description,
          displayName: oldTagData?.[0]?.displayName,
          objectiveLevel: oldTagData?.[0]?.objectiveLevel,
          objectiveTimeBoundId: oldTagData?.[0]?.objectiveTimeBoundId,
          objectiveType: oldTagData?.[0]?.objectiveType,
        }),
        newValue: JSON.stringify({
          description: tag.description,
          displayName: tag.displayName,
          objectiveLevel: tag.objectiveLevel,
          objectiveTimeBoundId: tag.objectiveTimeBoundId,
          objectiveType: tag.objectiveType,
        }),
      },
    });
    setLoading(true);
    // following fields will be sent to BE.

    updateObjective({ tag, updateAllInstances, renameObjective })
      .then((orgTags) => {
        getObjectiveData({ page });
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  // add tags
  const addTag = (tag) => {
    setLoading(true);
    // following fields will be sent to BE.
    saveObjective({ tag })
      .then((orgTags) => {
        getObjectiveData({ page });
        removeNewTag();
        dispatch(showSuccessSnackbar('Objective added successfully'));
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  const toggleEditing = (objectiveId) => {
    removeNewTag();
    setTags((tags) =>
      tags.map((tag) => {
        if (tag.objectiveId === objectiveId) {
          if (!tag.isEditing) {
            setEditingTags((prev) => [...prev, tag.objectiveId]);
          } else {
            setEditingTags((prev) => prev.filter((_t) => _t !== tag.objectiveId));
          }
          tag.isEditing = !tag.isEditing;
        }
        return tag;
      })
    );
  };

  const toggleRenaming = (objectiveId) => {
    removeNewTag();
    setTags((tags) =>
      tags.map((tag) => {
        if (tag.objectiveId === objectiveId) {
          if (!tag.isRenaming) {
            setRenamingTags((prev) => [...prev, tag.objectiveId]);
          } else {
            setRenamingTags((prev) => prev.filter((_t) => _t !== tag.objectiveId));
          }
          tag.isRenaming = !tag.isRenaming;
        }
        return tag;
      })
    );
  };

  const addNewTag = () => {
    setNewTag(new OrgObjective({}));
    setAddNew(true);
    setTags(
      tags.map((tag) => {
        tag.isEditing = false;
        return tag;
      })
    );
  };

  const removeNewTag = () => {
    setAddNew(false);
    setNewTag(new OrgObjective({}));
  };

  const disableExpansion = (objectiveId) => {
    setTags(
      tags.map((tag) => {
        if (objectiveId === tag.objectiveId) tag.hasChildTags = false;
        return tag;
      })
    );
  };

  const enableExpansion = (objectiveId) => {
    setAllTags((allTags) =>
      allTags.map((tag) => {
        if (objectiveId === tag.objectiveId) tag.hasChildTags = true;
        return tag;
      })
    );
  };

  useEffect(() => {
    if (addNew) {
      if (!editingTags.includes('createNew+++null')) {
        setNewTag(new OrgObjective({}));
        setEditingTags((prev) => [...prev, 'createNew+++null']);
        // not sure what it does
        setRenamingTags((prev) => [...prev, 'createNew+++null']);
      }
    } else {
      setEditingTags((prev) => prev.filter((tagId) => tagId !== 'createNew+++null'));
      // not sure what it does
      setRenamingTags((prev) => prev.filter((tagId) => tagId !== 'createNew+++null'));
    }
  }, [addNew]);

  const onChangeTimePeriod = useCallback(
    (selectedTimePeriod) => {
      setSelectedTimeBoundPeriod(selectedTimePeriod);
      setPage(0);
      setPaginatedTags(0, allTags, rowsPerPage);
    },
    [allTags, rowsPerPage, setSelectedTimeBoundPeriod]
  );

  const ModalHeader = ({ className }) => (
    <VerticallyAlignedFlexContainer>
      <Typography6 weight={400}>Select Objective</Typography6>
      {/* <Typography4 color={theme.palette.text.dark1} topGutter={1}>
          Select or create an objective for your goal
        </Typography4> */}
    </VerticallyAlignedFlexContainer>
  );

  const headerComp = useMemo(() => {
    if (!selectedTimeBoundPeriod) {
      return <Loader singleLine />;
    }
    return (
      <TableHeaderWrapper $stickyHeader={isModal}>
        {allTags.length === 0 || isModal ? (
          <div />
        ) : (
          <SearchTextField
            text={searchString}
            width="40%"
            onChange={(val) => {
              setSearchString(val);
              setPage(0);
            }}
            placeholder="Search parent objectives"
          />
        )}
        {isModal && <ModalHeader />}
        <div style={{ marginLeft: 'auto' }}>
          <TimePeriodDropDown
            timePeriodData={timePeriodData}
            selectedTimeBoundPeriod={selectedTimeBoundPeriod}
            disabled={!allowTimePeriodChange}
            setSelectedTimeBoundPeriod={(timePeriod) => {
              sendClickedOnTimePeriodEvent({
                eventProperties: {
                  source: 'Settings',
                },
              });
              onChangeTimePeriod(timePeriod);
            }}
          />
        </div>
        {isModal && (
          <CrossIcon
            size="fixed16px"
            style={{ marginLeft: '1.5rem' }}
            onClick={onClose}
            color={theme.palette.icons.mediumGrey}
          />
        )}
      </TableHeaderWrapper>
    );
  }, [searchString, selectedTimeBoundPeriod, timePeriodData, allTags.length]);

  if (loading || timePeriodData.length === 0) {
    return <Loader />;
  }

  const onImportHandler = ({ sourceTimePeriod, targetTimePeriod }) => {
    importTimePeriodObjectives({ sourceTimePeriod, targetTimePeriod }).then((res) => getObjectiveData());
  };

  return (
    <>
      <>
        {headerComp}
        {allTags.length === 0 && !addNew ? (
          <NoObjectivesView
            timePeriodData={timePeriodData}
            selectedTimeBoundPeriod={selectedTimeBoundPeriod}
            onImport={onImportHandler}
            onNewClick={onNewClick}
            modalView={modalView}
            onClose={onClose}
            allowTimePeriodChange={allowTimePeriodChange}
            enableCreation={enableCreation}
          />
        ) : (
          <StyledTable stickyHeader aria-label="List of objectives">
            <HeaderComponent isModal={isModal} modalView={modalView} />
            <TableBody>
              {addNew && enableCreation && (
                <NewEditableTagRow
                  key="create-new-row"
                  tag={{ ...newTag, timeBoundDetails: selectedTimeBoundPeriod, isEditing: true }}
                  onCancel={removeNewTag}
                  onUpdate={addTag}
                  isExpandable
                  isEditing
                  toggleEditing={toggleEditing}
                  toggleRenaming={toggleRenaming}
                  enableExpansion={enableExpansion}
                  hasChildTags={false}
                  canHaveChildTags={false}
                  modalView={modalView}
                  getFocus
                  editingTags={editingTags}
                  renamingTags={renamingTags}
                  setEditingTags={setEditingTags}
                  setRenamingTags={setRenamingTags}
                  timePeriodData={timePeriodData}
                  refreshCurrentPage={() => getObjectiveData({ page })}
                  selectedTimeBoundPeriod={selectedTimeBoundPeriod}
                  searchString={searchString}
                  timePeriodsList={timePeriodsList}
                />
              )}
              {!addNew && enableCreation && <CreateNewObjectiveRow displayNewTagCreationRow={addNewTag} />}
              {tags.map((tag, index) => (
                <NewEditableTagRow
                  key={tag.objectiveId}
                  tag={tag}
                  // onActiveToggle={handleToggleActive}
                  onUpdate={updateTag}
                  isExpandable
                  isEditing={tag.isEditing}
                  isRenaming={tag.isRenaming}
                  toggleEditing={toggleEditing}
                  toggleRenaming={toggleRenaming}
                  disableExpansion={() => disableExpansion(tag.objectiveId)}
                  enableExpansion={() => enableExpansion(tag.objectiveId)}
                  hasChildTags={tag.hasChildTags}
                  canHaveChildTags
                  modalView={modalView}
                  getFocus={false}
                  enableCreation={enableCreation}
                  selectTag={onSelect}
                  levelsForWhichLinesToBeDrawn={[]}
                  isLastRow={index === tags.length - 1}
                  editingTags={editingTags}
                  renamingTags={renamingTags}
                  setEditingTags={setEditingTags}
                  setRenamingTags={setRenamingTags}
                  timePeriodData={timePeriodData}
                  refreshCurrentPage={() => getObjectiveData({ page })}
                  selectedTimeBoundPeriod={selectedTimeBoundPeriod}
                  searchString={searchString}
                  selectedObjective={selectedObjective}
                  selectedObjectiveHierarchy={selectedObjectiveHierarchy}
                  isScrolledToSelected={isScrolledToSelected}
                  setIsScrolledToSelected={setIsScrolledToSelected}
                  timePeriodsList={timePeriodsList}
                />
              ))}
            </TableBody>
          </StyledTable>
        )}
      </>

      {allTags.length !== 0 && (
        <StyledBottomBar>
          <TablePagination
            component="div"
            count={getSearchAppliedTags(allTags, searchString).length}
            rowsPerPage={rowsPerPage}
            page={page}
            rowsPerPageOptions={[]}
            onChangePage={(e, newPage) => {
              setPage(newPage);
              setPaginatedTags(newPage, allTags, rowsPerPage);
            }}
            labelRowsPerPage={null}
            style={{ marginLeft: 'auto' }}
          />
        </StyledBottomBar>
      )}
    </>
  );
};

const StyledTable = styled(Table)`
  .MuiTableCell-stickyHeader {
    background: ${theme.palette.background.paper};
  }
`;

export default TagsList;
